/**
 * Legal-page country resolver.
 *
 * Cascade (first non-null wins):
 *   1. URL `?country=` (deep link / share / cross-domain handoff)
 *   2. Entity country  — provider.countryCode | facility.countryCode | business.countryCode
 *   3. Browsing country — atom (cookie pontimed_country)
 *   4. DEFAULT_COUNTRY — "ro"
 *
 * Why entity > browsing on legal: a doctor licensed in RO traveling in BG
 * should see the DPA of his practice country (regulator-relevant) — NOT
 * the country he happens to be browsing from. URL beats entity so a
 * shared link can force a specific country variant.
 *
 * V1 limitation: portal does not yet expose entity profiles via session/JWT
 * (provider/facility/business profiles are fetched on demand). Step 2 of
 * the cascade therefore falls back to browsing for now; entity awareness
 * will land with P10 (entity-country onboarding).
 */

import { useSearch } from "@tanstack/react-router";

import { DEFAULT_COUNTRY, isSupportedCountry } from "@pontimed/types";

import { useBrowsingCountry } from "@/lib/hooks/use-browsing-country";

interface SearchWithCountry {
  country?: string;
}

export function useLegalCountry(): string {
  const search = useSearch({ strict: false }) as SearchWithCountry;
  const { country: browsingCountry } = useBrowsingCountry();

  // 1. URL wins — supports deep links and the cross-domain hand-off
  //    apps/web → portal that includes `?country=ro` query string.
  const fromUrl = search.country?.toLowerCase();
  if (fromUrl && isSupportedCountry(fromUrl)) return fromUrl;

  // 2. Entity country — TODO P10. When provider/facility/business profiles
  //    surface in session, prefer the user's regulatory country here.

  // 3. Browsing — header switcher / cookie / cf-ipcountry chain.
  if (browsingCountry && isSupportedCountry(browsingCountry)) return browsingCountry;

  // 4. Default.
  return DEFAULT_COUNTRY;
}
